import {
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  getDefaultZIndex,
  Loader,
  Popover,
  Text,
  Tooltip,
} from "@mantine/core";
import dayjs from "dayjs";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { useSelector } from "src/redux/hooks";
import type { IPendingRequest } from "src/redux/slices/pendingRequestsSlice";
import { groupPendingRequests } from "src/utils/groupPendingRequests";
import { logPendingNav } from "src/utils/logPendingNav";

const POPOVER_Z_INDEX = getDefaultZIndex("popover");
const TOOLTIP_Z_INDEX = getDefaultZIndex("max");
const pendingTooltipProps = {
  withinPortal: true,
  zIndex: TOOLTIP_Z_INDEX,
  floatingStrategy: "fixed" as const,
  portalProps: {
    reuseTargetNode: false,
    style: { zIndex: TOOLTIP_Z_INDEX },
  },
} as const;

const getStatusLabel = (status: string) => {
  switch (status) {
    case "pending":
      return "Pending";
    case "uploading":
      return "Uploading";
    case "done":
      return "Done";
    case "failed":
      return "Failed";
    case "pending-new-entry":
      return "Waiting for entry";
    default:
      return status;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "done":
      return "green";
    case "failed":
      return "red";
    case "uploading":
      return "blue";
    case "pending-new-entry":
      return "gray";
    default:
      return "yellow";
  }
};

// `uploading` is retryable because a crash mid-replay strands rows in it.
// Retrying one genuinely in flight is a no-op thanks to processPendingUpload.
const isRetryable = (status: string) =>
  status === "failed" || status === "uploading";

const UploadStatusBadge = ({ status }: { status: string }) => {
  if (status === "uploading") {
    return (
      <Flex align="center" gap={6}>
        <Loader size="xs" />
        <Text size="xs">Uploading</Text>
      </Flex>
    );
  }

  return (
    <Badge size="xs" color={getStatusColor(status)} variant="light">
      {getStatusLabel(status)}
    </Badge>
  );
};

const RowActions = ({
  req,
  onRetry,
}: {
  req: IPendingRequest;
  onRetry: (reqId: string) => void;
}) => (
  <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
    {req.queuedCount && req.queuedCount > 1 ? (
      <Tooltip
        {...pendingTooltipProps}
        disabled={req.status === "done"}
        label={`${req.queuedCount} ${req.queuedNoun} for this entry are waiting to upload, oldest first.`}
        multiline
        w={240}>
        <Badge
          size="xs"
          color={req.status === "done" ? "green" : "gray"}
          variant="outline"
          style={req.status === "done" ? { cursor: "default" } : undefined}>
          {req.queuedCount} {req.queuedNoun}
        </Badge>
      </Tooltip>
    ) : null}
    {req.isOtherUser ? (
      <Tooltip
        {...pendingTooltipProps}
        label="Queued by another user. They need to sign in here to upload it."
        multiline
        w={240}>
        <Badge size="xs" color="gray" variant="light">
          Other user
        </Badge>
      </Tooltip>
    ) : null}
    <UploadStatusBadge status={req.status} />
    {!req.isOtherUser && isRetryable(req.status) ? (
      <Button
        size="compact-xs"
        variant="light"
        onClick={() => onRetry(req.req)}>
        Retry
      </Button>
    ) : null}
  </Flex>
);

const NestedRowMarker = () => (
  <Box
    ml={10}
    py={6}
    style={{
      display: "flex",
      alignSelf: "stretch",
      flexShrink: 0,
    }}>
    <Box
      w={2}
      style={{
        alignSelf: "stretch",
        backgroundColor: "var(--mantine-color-gray-4)",
        borderRadius: 1,
      }}
    />
  </Box>
);

const UploadRow = ({
  req,
  onNavigate,
  onRetry,
  nested = false,
}: {
  req: IPendingRequest;
  onNavigate: (route: string | null | undefined, req: IPendingRequest) => void;
  onRetry: (reqId: string) => void;
  nested?: boolean;
}) => {
  const label = req.action;
  const createdLabel = dayjs(req.created).format("D MMM YYYY HH:mm");
  const uploadedLabel =
    req.uploaded && req.status === "done"
      ? dayjs(req.uploaded).format("D MMM YYYY HH:mm")
      : null;

  return (
    <Flex align="center" justify="space-between" gap="sm" py={4}>
      <Flex
        align="center"
        gap={10}
        style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
        {nested ? <NestedRowMarker /> : null}
        <Flex direction="column" gap={2} style={{ flex: 1, minWidth: 0 }}>
          {req.route ? (
            <Anchor
              component="button"
              type="button"
              size="xs"
              ta="left"
              lineClamp={2}
              onClick={() => onNavigate(req.route, req)}>
              {label}
            </Anchor>
          ) : (
            <Text size="xs" lineClamp={2}>
              {label}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            {createdLabel}
            {uploadedLabel ? ` · Uploaded ${uploadedLabel}` : ""}
          </Text>
          {req.status === "failed" && req.error ? (
            <Tooltip {...pendingTooltipProps} label={req.error} multiline w={280}>
              <Text size="xs" c="red" lineClamp={2}>
                {req.error}
              </Text>
            </Tooltip>
          ) : null}
        </Flex>
      </Flex>
      <RowActions req={req} onRetry={onRetry} />
    </Flex>
  );
};

const LogbookHeaderRow = ({
  logbookName,
  route,
  onNavigate,
}: {
  logbookName: string;
  route?: string | null;
  onNavigate: (route: string | null | undefined) => void;
}) => (
  <Flex align="center" justify="space-between" gap="sm" py={4}>
    <Flex style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
      {route ? (
        <Anchor
          component="button"
          type="button"
          size="xs"
          fw={600}
          ta="left"
          lineClamp={2}
          onClick={() => onNavigate(route)}>
          {logbookName}
        </Anchor>
      ) : (
        <Text size="xs" fw={600} lineClamp={2}>
          {logbookName}
        </Text>
      )}
    </Flex>
  </Flex>
);

const PendingRequestsInfo = () => {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();

  const pendingRequests = useSelector(
    state => state.pendingRequestsSlice.pendingRequests,
  );

  const displayItems = useMemo(
    () => groupPendingRequests(pendingRequests),
    [pendingRequests],
  );

  const uploadSummary = useMemo(() => {
    const pendingCount = pendingRequests.filter(req =>
      ["pending", "uploading", "failed"].includes(req.status),
    ).length;
    const doneCount = pendingRequests.filter(
      req => req.status === "done",
    ).length;

    if (pendingCount > 0) {
      return `Requests: ${pendingRequests.length} (Pending: ${pendingCount})`;
    }

    return `Requests: ${pendingRequests.length}${doneCount > 0 ? ` · Done: ${doneCount}` : ""}`;
  }, [pendingRequests]);

  const hasCompletedRequests = useMemo(
    () => pendingRequests.some(req => req.status === "done"),
    [pendingRequests],
  );

  const toggle = useCallback(() => {
    setOpened(prev => !prev);
  }, []);

  const handleNavigate = useCallback(
    (route: string | null | undefined, req: IPendingRequest) => {
      logPendingNav("click", {
        action: req.action,
        req: req.req,
        name: req.name,
        status: req.status,
        route,
        serverEntryId: req.serverEntryId,
        queuedCount: req.queuedCount,
      });

      if (!route) {
        logPendingNav("click ignored: no route", { req: req.req });
        return;
      }
      setOpened(false);
      navigate(route);
    },
    [navigate],
  );

  const handleRetry = useCallback(async (reqId: string) => {
    if (!window.api?.retryUpload) {
      return;
    }

    await window.api.retryUpload(reqId);
  }, []);

  const handleClearCompleted = useCallback(async () => {
    if (!window.api?.clearCompletedRequests) {
      return;
    }

    await window.api.clearCompletedRequests();
  }, []);

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Popover opened={opened} onChange={setOpened} keepMounted zIndex={POPOVER_Z_INDEX}>
      <Popover.Target>
        <Button ml="auto" variant="white" onClick={toggle}>
          {uploadSummary}
        </Button>
      </Popover.Target>
      <Popover.Dropdown
        maw={480}
        mah={400}
        style={{ overflowY: "auto", width: "100%" }}>
        {hasCompletedRequests ? (
          <>
            <Flex justify="flex-end" pb="xs">
              <ConfirmationButton
                modalText="Are you sure you want to clear out completed requests ?"
                onConfirm={handleClearCompleted}
                buttonProps={{ size: "compact-xs", variant: "subtle" }}>
                Clear completed
              </ConfirmationButton>
            </Flex>
            <Divider />
          </>
        ) : null}
        {displayItems.map(item => {
          if (item.kind === "row") {
            return (
              <Fragment key={item.req.req}>
                <UploadRow
                  req={item.req}
                  onNavigate={handleNavigate}
                  onRetry={handleRetry}
                />
                <Divider />
              </Fragment>
            );
          }

          const { group } = item;
          const headerRoute =
            group.save?.route ?? group.checklists[0]?.route ?? null;
          const hasParent = Boolean(group.save || group.logbookName);

          return (
            <Fragment key={`group-${group.entryId}`}>
              {group.save ? (
                <UploadRow
                  req={group.save}
                  onNavigate={handleNavigate}
                  onRetry={handleRetry}
                />
              ) : group.logbookName ? (
                <LogbookHeaderRow
                  logbookName={group.logbookName}
                  route={headerRoute}
                  onNavigate={route => {
                    if (!route) return;
                    setOpened(false);
                    navigate(route);
                  }}
                />
              ) : null}
              {group.checklists.map(req => (
                <UploadRow
                  key={req.req}
                  req={req}
                  nested={hasParent}
                  onNavigate={handleNavigate}
                  onRetry={handleRetry}
                />
              ))}
              <Divider />
            </Fragment>
          );
        })}
      </Popover.Dropdown>
    </Popover>
  );
};

export default PendingRequestsInfo;
